/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Brain, Clock, Target, Play, RotateCcw, CheckCircle, XCircle, Trophy } from 'lucide-react';
import { FretPosition } from '../Fretboard';
import FretboardQuiz from './FretboardQuiz';
import { QuizBannerState } from './NoteRecognitionQuiz';

interface FretboardTestProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
  tuning: string[];
  // Fretboard control props
  fretboardInteractive: boolean;
  setFretboardInteractive: (active: boolean) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  fretRange?: { start: number; end: number };
  isStandardTuning: boolean;
  onClearFretboard?: () => void;
  /** When true, shows compact overlay banner instead of full Card UI */
  isMobile?: boolean;
  /** Callback to expose quiz state to parent for mobile banner rendering */
  onQuizStateChange?: (state: QuizBannerState) => void;
}

interface TestSettings {
  duration: number; // in seconds
  questionsLimit: number;
  testType: 'timed' | 'questions';
}

interface TestResult {
  questionsAnswered: number;
  correctAnswers: number;
  timeSpent: number;
  accuracy: number;
  completedAt: Date;
}

type TestState = 'setup' | 'active' | 'completed';

const FretboardTest: React.FC<FretboardTestProps> = ({
  currentKey,
  currentScale,
  noteCollection,
  tuning,
  fretboardInteractive,
  setFretboardInteractive,
  setRootNotePosition,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  fretRange = { start: 0, end: 12 },
  isStandardTuning,
  onClearFretboard,
  isMobile = false,
  onQuizStateChange
}) => {
  const [testState, setTestState] = useState<TestState>('setup');
  const [testSettings, setTestSettings] = useState<TestSettings>({
    duration: 180, // 3 minutes
    questionsLimit: 10,
    testType: 'questions'
  });
  const [timeRemaining, setTimeRemaining] = useState(testSettings.duration);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (testState === 'active' && testSettings.testType === 'timed') {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            endTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [testState, testSettings.testType]);

  // Clear the fretboard state when this component mounts (but don't disable interactive mode)
  useEffect(() => {
    // Note: Don't call onClearFretboard() here as it disables interactive mode.
    // Just clear the visual state.
    setUserFretboardAnswers([]);
    setRootNotePosition(null);
    setHighlightedFretPositions([]);
  }, []);

  const startTest = () => {
    setTestState('active');
    setStartTime(new Date());
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setTimeRemaining(testSettings.duration);
    setTestResult(null);

    // Clear ALL fretboard state when starting the test
    setUserFretboardAnswers([]);
    setCorrectUserFretboardAnswers([]);
    setIncorrectUserFretboardAnswers([]);
    setHighlightedFretPositions([]);
    setRootNotePosition(null);

    // Enable interactive mode AFTER clearing state
    // Note: Don't call onClearFretboard() as it disables interactive mode
    setFretboardInteractive(true);
  };

  const endTest = () => {
    setTestState('completed');
    setFretboardInteractive(false);
    
    const timeSpent = startTime ? Math.round((Date.now() - startTime.getTime()) / 1000) : 0;
    const accuracy = questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0;
    
    const result: TestResult = {
      questionsAnswered,
      correctAnswers,
      timeSpent,
      accuracy,
      completedAt: new Date()
    };
    
    setTestResult(result);
    
    // Save to localStorage for progress tracking
    const existingResults = JSON.parse(localStorage.getItem('fretboardTestResults') || '[]');
    existingResults.push(result);
    localStorage.setItem('fretboardTestResults', JSON.stringify(existingResults));
  };

  const resetTest = () => {
    setTestState('setup');
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setTimeRemaining(testSettings.duration);
    setTestResult(null);
    setStartTime(null);
  };

  const handleScoreUpdate = (correct: number, total: number) => {
    console.log(`Score Update - Correct: ${correct}, Total: ${total}, Limit: ${testSettings.questionsLimit}`);
    setQuestionsAnswered(total);
    setCorrectAnswers(correct);
    
    // Check if we should end the test based on question limit
    if (testSettings.testType === 'questions' && total >= testSettings.questionsLimit) {
      console.log(`Test should end - Total: ${total}, Limit: ${testSettings.questionsLimit}`);
      setTimeout(() => endTest(), 1000); // Give time to show the final result
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressValue = (): number => {
    if (testSettings.testType === 'timed') {
      return ((testSettings.duration - timeRemaining) / testSettings.duration) * 100;
    } else {
      return (questionsAnswered / testSettings.questionsLimit) * 100;
    }
  };

  if (testState === 'setup') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Fretboard Test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Test Type Selection */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3">Choose Test Type</h4>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="testType"
                  value="questions"
                  checked={testSettings.testType === 'questions'}
                  onChange={(e) => setTestSettings(prev => ({ ...prev, testType: e.target.value as 'questions' }))}
                  className="w-4 h-4"
                />
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Fixed Questions ({testSettings.questionsLimit} questions)</div>
                    <div className="text-sm text-muted-foreground">Answer a set number of questions at your own pace</div>
                  </div>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="testType"
                  value="timed"
                  checked={testSettings.testType === 'timed'}
                  onChange={(e) => setTestSettings(prev => ({ ...prev, testType: e.target.value as 'timed' }))}
                  className="w-4 h-4"
                />
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Timed Test ({formatTime(testSettings.duration)})</div>
                    <div className="text-sm text-muted-foreground">Answer as many questions as possible within the time limit</div>
                  </div>
                </div>
              </label>
            </div>
          </Card>

          {/* Test Settings */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3">Settings</h4>
            <div className="space-y-3">
              {testSettings.testType === 'questions' && (
                <div>
                  <label className="text-sm font-medium">Number of Questions</label>
                  <select
                    value={testSettings.questionsLimit}
                    onChange={(e) => setTestSettings(prev => ({ ...prev, questionsLimit: parseInt(e.target.value) }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value={10}>10 Questions</option>
                    <option value={20}>20 Questions</option>
                    <option value={30}>30 Questions</option>
                    <option value={50}>50 Questions</option>
                  </select>
                </div>
              )}
              
              {testSettings.testType === 'timed' && (
                <div>
                  <label className="text-sm font-medium">Test Duration</label>
                  <select
                    value={testSettings.duration}
                    onChange={(e) => setTestSettings(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value={180}>3 Minutes</option>
                    <option value={300}>5 Minutes</option>
                    <option value={600}>10 Minutes</option>
                    <option value={900}>15 Minutes</option>
                  </select>
                </div>
              )}
            </div>
          </Card>

          {/* Start Button */}
          <Card className="p-4 text-center">
            <Button onClick={startTest} size="lg" className="w-full">
              <Play className="h-4 w-4 mr-2" />
              Start Test
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              Click to begin your fretboard knowledge test
            </p>
          </Card>
        </CardContent>
      </Card>
    );
  }

  if (testState === 'completed' && testResult) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Test Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Results Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{testResult.accuracy}%</div>
              <div className="text-sm text-muted-foreground">Accuracy</div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{testResult.correctAnswers}</div>
              <div className="text-sm text-muted-foreground">Correct Answers</div>
            </Card>
          </div>

          {/* Detailed Stats */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3">Test Details</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Questions Answered:</span>
                <span className="font-medium">{testResult.questionsAnswered}</span>
              </div>
              <div className="flex justify-between">
                <span>Correct Answers:</span>
                <span className="font-medium text-green-600">{testResult.correctAnswers}</span>
              </div>
              <div className="flex justify-between">
                <span>Incorrect Answers:</span>
                <span className="font-medium text-red-600">{testResult.questionsAnswered - testResult.correctAnswers}</span>
              </div>
              <div className="flex justify-between">
                <span>Time Spent:</span>
                <span className="font-medium">{formatTime(testResult.timeSpent)}</span>
              </div>
              <div className="flex justify-between">
                <span>Average Time per Question:</span>
                <span className="font-medium">
                  {testResult.questionsAnswered > 0 ? formatTime(Math.round(testResult.timeSpent / testResult.questionsAnswered)) : 'N/A'}
                </span>
              </div>
            </div>
          </Card>

          {/* Performance Feedback */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="text-center space-y-2">
              <h4 className="font-semibold text-blue-900">
                {testResult.accuracy >= 90 && "🏆 Excellent Performance!"}
                {testResult.accuracy >= 80 && testResult.accuracy < 90 && "🎯 Great Job!"}
                {testResult.accuracy >= 70 && testResult.accuracy < 80 && "👍 Good Work!"}
                {testResult.accuracy >= 60 && testResult.accuracy < 70 && "📈 Keep Practicing!"}
                {testResult.accuracy < 60 && "💪 Room for Improvement!"}
              </h4>
              <p className="text-sm text-blue-800">
                {testResult.accuracy >= 90 && "You have excellent fretboard knowledge! Keep challenging yourself with more advanced exercises."}
                {testResult.accuracy >= 80 && testResult.accuracy < 90 && "Strong performance! A little more practice will get you to expert level."}
                {testResult.accuracy >= 70 && testResult.accuracy < 80 && "You're building solid fundamentals. Focus on areas where you made mistakes."}
                {testResult.accuracy >= 60 && testResult.accuracy < 70 && "Good progress! Regular practice will help improve your accuracy."}
                {testResult.accuracy < 60 && "Everyone starts somewhere! Keep practicing in the Practice tab to build your skills."}
              </p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-2 justify-center">
            <Button onClick={resetTest} variant="outline">
              <RotateCcw className="h-4 w-4 mr-2" />
              Take Another Test
            </Button>
            <Button onClick={() => setTestState('setup')}>
              Change Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Active test state
  return (
    <div className="space-y-4">
      {/* Test Header with Progress */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Fretboard Test in Progress
          </h3>
          <Button 
            onClick={endTest} 
            variant="outline" 
            size="sm"
          >
            End Test
          </Button>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>
              {testSettings.testType === 'questions' ? (
                `${questionsAnswered}/${testSettings.questionsLimit} questions`
              ) : (
                `${formatTime(timeRemaining)} remaining`
              )}
            </span>
          </div>
          <Progress value={getProgressValue()} className="h-2" />
        </div>

        <div className="flex items-center gap-4 mt-3 text-sm">
          <div className="flex items-center gap-1">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>Correct: {correctAnswers}</span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="h-4 w-4 text-red-600" />
            <span>Incorrect: {questionsAnswered - correctAnswers}</span>
          </div>
          <div className="flex items-center gap-1">
            <Target className="h-4 w-4 text-blue-600" />
            <span>Accuracy: {questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0}%</span>
          </div>
        </div>
      </Card>

      {/* Quiz Component */}
      <FretboardQuiz
        currentKey={currentKey}
        currentScale={currentScale}
        noteCollection={noteCollection}
        tuning={tuning}
        fretboardInteractive={fretboardInteractive}
        setFretboardInteractive={setFretboardInteractive}
        setRootNotePosition={setRootNotePosition}
        userFretboardAnswers={userFretboardAnswers}
        setUserFretboardAnswers={setUserFretboardAnswers}
        setHighlightedFretPositions={setHighlightedFretPositions}
        correctUserFretboardAnswers={correctUserFretboardAnswers}
        setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
        incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
        setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
        fretRange={fretRange}
        isStandardTuning={isStandardTuning}
        onScoreUpdate={handleScoreUpdate}
        testMode={true}
        isMobile={isMobile}
        onQuizStateChange={onQuizStateChange}
      />
    </div>
  );
};

export default FretboardTest;